import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { CallType } from "@scrile/api-provider/dist/api/CallsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import useJumpToMessage from "hooks/useJumpToMessage";
import useMessageSearch from "hooks/useMessageSearch";
import useMessageSelection from "hooks/useMessageSelection";
import useTypingIndicator from "hooks/useTypingIndicator";
import gqlFetch from "lib/gqlFetch";
import providers from "lib/providers";
import ChatHeader from "components/ChatHeader";
import ChatSearchBar from "components/ChatSearchBar";
import ChatSelectionToolbar from "components/ChatSelectionToolbar";
import ChatSkeleton from "components/ChatSkeleton";
import ForwardMessageModal from "components/ForwardMessageModal";
import MessageInput from "components/MessageInput";
import MessagesListView from "components/MessagesListView";
import PinnedMessagesBar from "components/PinnedMessagesBar";
import ScheduledMessages from "components/ScheduledMessages";
import useViewController from "./viewController";
import { Routes } from "../../../../router/config";
import "./style.scss";

interface Props {
  setSenderUser: (userName: UserPublicData | null) => void;
  onBack?: () => void;
}

function MessagesList({ setSenderUser, onBack }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const { userId } = useParams<{ userId: string }>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [forwardMessage, setForwardMessage] = useState<Message | null>(null);
  const [forwardBulk, setForwardBulk] = useState<Message[] | null>(null);
  const [callingInProgress, setCallingInProgress] = useState(false);
  const [mediaHighlightId, setMediaHighlightId] = useState<string | undefined>();
  const [pinnedMessages, setPinnedMessages] = useState<
    Array<{ messageId: string; text?: string; senderName?: string }>
  >([]);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());

  const { user: authUser } = useAuthUser();
  const {
    onFocus,
    messages,
    senderUser,
    loadMore,
    scrollMessages,
    loadingMessages,
    setObserve,
    minUnreadMessageId,
    showUnread,
    reverseContentRef,
    scrollToBottomFnRef,
    settleAtBottomFnRef,
    thread,
    updateMessage,
    deleteMessage,
    loadUntilMessage,
  } = useViewController(userId, setSenderUser);

  const { typingUsers, sendTyping, addTypingUser } = useTypingIndicator(thread?.id);

  // Live "typing…" receive path (send path already wired via sendTyping).
  useEffect(() => {
    if (!thread?.id) return;
    let sub: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    (async () => {
      try {
        const s = await providers.MessagesProvider.subscribeTypingIndicator(thread.id, (data) => {
          if (data?.userId) addTypingUser({ userId: String(data.userId), userName: data.userName || "" });
        });
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[chat] subscribeTypingIndicator failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [thread?.id, addTypingUser]);

  const {
    searchResults,
    searchIndex,
    searchQuery,
    searchHighlightId,
    search,
    navigate: searchNavigate,
    clearSearch,
    resetSearch,
  } = useMessageSearch(thread?.id);

  // Reset search on user change
  useEffect(() => {
    resetSearch();
    setSearchOpen(false);
  }, [userId, resetSearch]);

  // Ctrl/Cmd+F to toggle search (second press restores native browser search)
  const searchOpenRef = useRef(searchOpen);
  searchOpenRef.current = searchOpen;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        if (!searchOpenRef.current) {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      search(query, messages);
    },
    [search, messages]
  );

  const handleCloseSearch = useCallback(() => {
    setSearchOpen(false);
    clearSearch();
  }, [clearSearch]);

  // --- Pinned messages ---
  const loadPinnedMessages = useCallback(async (tid: string) => {
    try {
      const json = await gqlFetch(
        `query GetPinned($threadId: ID!) { pinnedMessages(threadId: $threadId) { messageId message { id text senderUser { screenName } } pinnedBy pinnedTime } }`,
        { threadId: tid }
      );
      const pins = json?.data?.pinnedMessages || [];
      setPinnedMessages(
        pins.map((p: any) => ({
          messageId: p.messageId,
          text: p.message?.text || "",
          senderName: p.message?.senderUser?.screenName || "",
        }))
      );
      setPinnedIds(new Set(pins.map((p: any) => String(p.messageId))));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (thread?.id) loadPinnedMessages(thread.id);
  }, [thread?.id, loadPinnedMessages]);

  const handleTogglePin = useCallback(
    async (message: Message) => {
      if (!thread?.id) return;
      try {
        await gqlFetch(
          `mutation TogglePin($messageId: ID!, $threadId: ID!) { togglePinMessage(messageId: $messageId, threadId: $threadId) }`,
          { messageId: message.id, threadId: thread.id }
        );
        await loadPinnedMessages(thread.id);
      } catch {
        /* ignore */
      }
    },
    [thread?.id, loadPinnedMessages]
  );

  // One creator for voice+video calls — the two ChatHeader handlers were
  // 23-line twins differing only in voiceOnly and the ?voiceOnly=1 suffix.
  const startCall = useCallback(
    async (voiceOnly: boolean) => {
      if (!senderUser || callingInProgress) return;
      setCallingInProgress(true);
      try {
        const response = await providers.CallsProvider.create({
          data: {
            receiverId: String(senderUser.id),
            subject: { subjectId: String(senderUser.id), subjectType: CallType.CALL },
          },
        });
        history.push(
          generatePath(Routes.callByCallId, { callId: String(response.call.id) }) + (voiceOnly ? "?voiceOnly=1" : ""),
          {
            callConnection: response.connection,
            isCaller: true,
            voiceOnly,
            opponentUser: senderUser,
          }
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[call] Failed to create ${voiceOnly ? "voice" : "video"} call:`, e);
      } finally {
        setCallingInProgress(false);
      }
    },
    [senderUser, callingInProgress, history]
  );

  const handleMediaScrollToMessage = useCallback((messageId: string) => {
    setMediaHighlightId(messageId);
    setTimeout(() => setMediaHighlightId(undefined), 3000);
  }, []);

  // --- Jump-to-message (reply-quote click / global-search result / pins) ---
  // Shared controller: captures the router-state target, pages history via
  // loadUntilMessage, then drives the highlightMessageId scroll+pulse.
  const { jumpHighlightId, jumpToMessage } = useJumpToMessage({
    chatKey: userId,
    // `!!thread` matches the group list's gate: a cache-hydrated chat can have
    // messages.length > 0 while `thread`/opponentIdRef are still null, so
    // without it the jump is consumed before loadUntilMessage can work — it
    // no-ops its retry budget, fails, and nothing re-fires the consume effect.
    ready: !loadingMessages && messages.length > 0 && !!thread,
    loadUntil: loadUntilMessage,
  });

  const activeHighlightId = useMemo(
    () => searchHighlightId || mediaHighlightId || jumpHighlightId,
    [searchHighlightId, mediaHighlightId, jumpHighlightId]
  );

  const handleUnpin = useCallback(
    async (messageId: string) => {
      if (!thread?.id) return;
      try {
        await gqlFetch(
          `mutation UnpinMsg($messageId: ID!, $threadId: ID!) { togglePinMessage(messageId: $messageId, threadId: $threadId) }`,
          { messageId, threadId: thread.id }
        );
        await loadPinnedMessages(thread.id);
      } catch {
        /* ignore */
      }
    },
    [thread?.id, loadPinnedMessages]
  );

  // Multi-select shared controller — 1-on-1 has no moderator role, so
  // only the user's OWN messages are deletable.
  const selection = useMessageSelection({
    messages,
    authUserId: authUser?.id,
    canModerate: false,
    onDeleted: (ids) => {
      ids.forEach((id) => deleteMessage(id));
    },
  });

  const handleBulkDelete = useCallback(async () => {
    if (!window.confirm(t("Delete {{count}} messages?", { count: selection.selectedCount }))) return;
    const { failed } = await selection.deleteSelected();
    if (failed.length > 0) {
      // eslint-disable-next-line no-alert
      alert(t("Failed to delete {{count}} message(s).", { count: failed.length }));
    }
  }, [selection, t]);

  return (
    <div className="messages-list">
      {selection.selectionMode ? (
        <ChatSelectionToolbar
          count={selection.selectedCount}
          canDeleteAny={selection.canDeleteAny}
          onDelete={handleBulkDelete}
          onForward={() => setForwardBulk(selection.selectedMessages)}
          onCancel={selection.cancel}
        />
      ) : (
        <ChatHeader
          user={senderUser}
          threadId={thread?.id}
          onBack={onBack}
          showBackButton={!!onBack}
          onToggleSearch={() => setSearchOpen(!searchOpen)}
          searchOpen={searchOpen}
          typingUsers={typingUsers}
          onScrollToMessage={handleMediaScrollToMessage}
          onVoiceCall={() => startCall(true)}
          onVideoCall={() => startCall(false)}
        />
      )}
      {searchOpen && !selection.selectionMode && (
        <ChatSearchBar
          onSearch={handleSearch}
          onNavigate={searchNavigate}
          onClose={handleCloseSearch}
          currentIndex={searchIndex}
          totalResults={searchResults.length}
        />
      )}
      {!searchOpen && pinnedMessages.length > 0 && (
        <PinnedMessagesBar
          pinnedMessages={pinnedMessages}
          onScrollToMessage={jumpToMessage}
          onUnpin={handleUnpin}
          canUnpin={true}
        />
      )}
      {messages.length > 0 && (
        <MessagesListView
          avatarSize={36}
          containerRef={reverseContentRef}
          scrollToBottomRef={scrollToBottomFnRef}
          settleAtBottomRef={settleAtBottomFnRef}
          messages={messages}
          infiniteScrollEnabled={scrollMessages}
          loadMore={loadMore}
          messageObserve={setObserve}
          showUnread={showUnread && !searchOpen}
          minUnreadMessageId={minUnreadMessageId}
          showScreenName={true}
          onMessageUpdated={updateMessage}
          onMessageDeleted={deleteMessage}
          opponentMinUnreadMessageId={thread?.opponentMinUnreadMessageId}
          highlightMessageId={activeHighlightId}
          searchQuery={searchOpen ? searchQuery : undefined}
          onReply={setReplyToMessage}
          onForward={setForwardMessage}
          onPin={handleTogglePin}
          onQuoteClick={jumpToMessage}
          pinnedMessageIds={pinnedIds}
          selectionMode={selection.selectionMode}
          selectedIds={selection.selectedIds}
          onToggleSelection={selection.toggle}
          onEnterSelection={(msg) => selection.enter(msg)}
        />
      )}
      {loadingMessages && <ChatSkeleton />}
      {!loadingMessages && messages.length === 0 && (
        <div className="messages-list__empty-text">{t("No messages here yet")}</div>
      )}
      {thread && <ScheduledMessages threadId={String(thread.id)} />}
      <MessageInput
        threadIds={thread ? [thread.id] : undefined}
        userIds={senderUser?.id ? [senderUser.id] : undefined}
        onFocus={onFocus}
        replyToMessage={replyToMessage}
        onClearReply={() => setReplyToMessage(null)}
        onTyping={() => sendTyping()}
      />
      {forwardMessage && <ForwardMessageModal messages={[forwardMessage]} onClose={() => setForwardMessage(null)} />}
      {forwardBulk && forwardBulk.length > 0 && (
        <ForwardMessageModal
          messages={forwardBulk}
          onClose={() => {
            setForwardBulk(null);
            selection.cancel();
          }}
        />
      )}
    </div>
  );
}

export default MessagesList;
